<script setup lang="ts">
import AppTabs from "@/components/shared/AppTabs.vue";
import CatMascot from "@/components/doctor-performance/cats/CatMascot.vue";
import CatSpeechBubble from "@/components/doctor-performance/cats/CatSpeechBubble.vue";
import DoctorPerfDrilldownDialog from "@/components/doctor-performance/DoctorPerfDrilldownDialog.vue";
import DoctorPerfSettingsDialog from "@/components/doctor-performance/DoctorPerfSettingsDialog.vue";
import { METRIC_LOOK, useCatPhrases, useSettingsWarningText } from "@/components/doctor-performance/doctorPerformanceUi";
import { metricOfField } from "@/utils/doctorPerformance";
import { DOCTOR_PERIOD_PRESETS, createDoctorPerformance, provideDoctorPerformance } from "@/composables/useDoctorPerformance";
import { DOCTOR_METRICS } from "@/type/doctorPerformance";
import type { TabItem } from "@/type/tabs";

/**
 * Doctor performance — what each doctor did, per day and per branch, calculated from clinical
 * records instead of a hand-typed sheet. Nine tabs share one filter bar and one loaded report
 * (`useDoctorPerformance`). Until `doctor_daily_report` is deployed the page runs on sample data
 * and says so with a chip; see docs/README.doctor-daily-report-backend-prompt.md.
 */

definePage({ meta: { permissionKey: "page.healthcare.doctor_performance" } });

const { t, locale } = useI18n({ useScope: "global" });
const { phrase } = useCatPhrases();

const activeTab = ref("");

const ctx = createDoctorPerformance({ openTab: (tab) => { activeTab.value = tab; } });
provideDoctorPerformance(ctx);

const tabs: TabItem[] = [
  { value: "table", labelKey: "alkokh.doctorPerformance.tabs.table", icon: "tabler-table", loader: () => import("@/components/doctor-performance/tabs/DoctorPerfTableTab.vue") },
  { value: "stats", labelKey: "alkokh.doctorPerformance.tabs.stats", icon: "tabler-chart-histogram", loader: () => import("@/components/doctor-performance/tabs/DoctorPerfStatsTab.vue") },
  { value: "suggestions", labelKey: "alkokh.doctorPerformance.tabs.suggestions", icon: "tabler-bulb", loader: () => import("@/components/doctor-performance/tabs/DoctorPerfSuggestionsTab.vue") },
  { value: "best", labelKey: "alkokh.doctorPerformance.tabs.best", icon: "tabler-trophy", loader: () => import("@/components/doctor-performance/tabs/DoctorPerfBestTab.vue") },
  { value: "time", labelKey: "alkokh.doctorPerformance.tabs.time", icon: "tabler-clock-hour-4", loader: () => import("@/components/doctor-performance/tabs/DoctorPerfTimeTab.vue") },
  { value: "doctor", labelKey: "alkokh.doctorPerformance.tabs.doctor", icon: "tabler-user-heart", loader: () => import("@/components/doctor-performance/tabs/DoctorPerfDoctorTab.vue") },
  { value: "battle", labelKey: "alkokh.doctorPerformance.tabs.battle", icon: "tabler-building-hospital", loader: () => import("@/components/doctor-performance/tabs/DoctorPerfBattleTab.vue") },
  { value: "calendar", labelKey: "alkokh.doctorPerformance.tabs.calendar", icon: "tabler-calendar", loader: () => import("@/components/doctor-performance/tabs/DoctorPerfCalendarTab.vue") },
  { value: "achievements", labelKey: "alkokh.doctorPerformance.tabs.achievements", icon: "tabler-medal", loader: () => import("@/components/doctor-performance/tabs/DoctorPerfAchievementsTab.vue") },
];

const presetItems = computed(() => DOCTOR_PERIOD_PRESETS.map(value => ({ value, title: t(`alkokh.doctorPerformance.period.${value}`) })));

// The mascot in the header changes its line every few seconds.
const headerLine = ref(0);
let ticker: ReturnType<typeof setInterval> | null = null;
onMounted(() => { ticker = setInterval(() => { headerLine.value += 1; }, 7000); });
onBeforeUnmount(() => { if (ticker) clearInterval(ticker); });
const busiest = computed(() => ctx.doctorAggregates.value[0]?.doctor_name ?? "");
const mascotLine = computed(() => phrase("header", headerLine.value, { name: busiest.value || t("alkokh.doctorPerformance.title") }));

// Definitions quote the counting rules currently in force, so the popover never drifts from them.
const definitionParams = computed(() => {
  const c = ctx.settings.value.counting;
  const list = (items: string[]) => items.join(", ") || "—";

  return {
    priorities: list(c.importantPriorities.map(p => t(`alkokh.doctorPerformance.settings.priorities.${p}`))),
    categories: list(c.surgeryCategories),
    modalities: list(c.xrayModalities),
    rule: t(`alkokh.doctorPerformance.settings.counting.boarding.${c.boardingRule}`),
    labRule: t(`alkokh.doctorPerformance.settings.counting.lab.${c.labRule}`),
    labTests: c.labTests.length ? list(c.labTests) : t("alkokh.doctorPerformance.settings.counting.labTestsAll"),
  };
});

const warningText = useSettingsWarningText();
const countingProblems = computed(() => ctx.settingsWarnings.value.map((warning) => {
  const metric = metricOfField(warning.field);

  return {
    key: `${warning.field}|${warning.value}`,
    metric: metric ? t(`alkokh.doctorPerformance.metrics.${metric}`) : "",
    text: warningText(warning),
  };
}));

// An older server leaves new metrics (lab tests) out; their zeros are not real, so say so.
const missingMetricNames = computed(() => new Intl.ListFormat(locale.value, { type: "conjunction" })
  .format(ctx.report.value.missingMetrics.map(m => t(`alkokh.doctorPerformance.metrics.${m}`))));

const showTabs = computed(() => ctx.state.value === "ok" && ctx.loaded.value);
const firstLoad = computed(() => !ctx.loaded.value);
</script>

<template>
  <div class="dp-page">
    <VCard
      elevation="0"
      border
      class="dp-header mb-4"
    >
      <div class="dp-head">
        <div class="dp-head__text">
          <h1 class="text-h4 d-flex align-center gap-2 flex-wrap">
            {{ t("alkokh.doctorPerformance.title") }}
            <VChip
              v-if="ctx.report.value.source === 'mock'"
              color="warning"
              variant="tonal"
              size="small"
              prepend-icon="tabler-flask"
            >
              {{ t("alkokh.doctorPerformance.sampleData") }}
            </VChip>
          </h1>
          <p class="text-body-2 text-medium-emphasis mb-0">
            {{ t("alkokh.doctorPerformance.subtitle") }}
          </p>
        </div>
        <div class="dp-head__mascot">
          <CatSpeechBubble
            :text="mascotLine"
            tail="end"
          />
          <CatMascot
            pose="sit"
            color="orange"
            accessory="stethoscope"
            :size="92"
          />
        </div>
      </div>

      <div class="dp-filters">
        <AppSelect
          v-model="ctx.preset.value"
          :items="presetItems"
          density="compact"
          :label="t('alkokh.doctorPerformance.period.label')"
          class="dp-filters__item"
        />
        <template v-if="ctx.preset.value === 'custom'">
          <AppDateTimePicker
            v-model="ctx.customFrom.value"
            density="compact"
            :label="t('alkokh.doctorPerformance.period.from')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            class="dp-filters__item"
          />
          <AppDateTimePicker
            v-model="ctx.customTo.value"
            density="compact"
            :label="t('alkokh.doctorPerformance.period.to')"
            :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
            class="dp-filters__item"
          />
        </template>
        <AppSelect
          v-model="ctx.branches.value"
          :items="ctx.branchItems.value"
          multiple
          chips
          closable-chips
          clearable
          density="compact"
          :label="t('alkokh.doctorPerformance.filters.branches')"
          :placeholder="t('alkokh.doctorPerformance.filters.allBranches')"
          class="dp-filters__item"
        />
        <AppSelect
          v-model="ctx.doctors.value"
          :items="ctx.doctorItems.value"
          multiple
          chips
          closable-chips
          clearable
          density="compact"
          :label="t('alkokh.doctorPerformance.filters.doctors')"
          :placeholder="t('alkokh.doctorPerformance.filters.allDoctors')"
          class="dp-filters__item dp-filters__item--wide"
        />
        <div class="dp-filters__actions">
          <VProgressCircular
            v-if="ctx.loading.value"
            indeterminate
            size="20"
            width="2"
            color="primary"
          />
          <VBtn
            icon="tabler-refresh"
            variant="text"
            size="small"
            :aria-label="t('alkokh.doctorPerformance.actions.refresh')"
            @click="ctx.load"
          />
          <VBtn
            icon="tabler-settings"
            variant="text"
            size="small"
            :aria-label="t('alkokh.doctorPerformance.settings.title')"
            @click="ctx.openSettings()"
          />
          <VMenu
            location="bottom end"
            max-width="420"
          >
            <template #activator="{ props: menu }">
              <VBtn
                v-bind="menu"
                icon="tabler-info-circle"
                variant="text"
                size="small"
                :aria-label="t('alkokh.doctorPerformance.definitions.title')"
              />
            </template>
            <VCard>
              <VCardItem>
                <VCardTitle>{{ t("alkokh.doctorPerformance.definitions.title") }}</VCardTitle>
                <VCardSubtitle class="text-wrap">
                  {{ t("alkokh.doctorPerformance.definitions.subtitle") }}
                </VCardSubtitle>
              </VCardItem>
              <VCardText>
                <div
                  v-for="m in DOCTOR_METRICS"
                  :key="m"
                  class="dp-def"
                >
                  <VIcon
                    :icon="METRIC_LOOK[m].icon"
                    :color="METRIC_LOOK[m].color"
                    size="18"
                  />
                  <div>
                    <div class="font-weight-bold text-body-2">
                      {{ t(`alkokh.doctorPerformance.metrics.${m}`) }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      {{ t(`alkokh.doctorPerformance.definitions.${m}`, definitionParams) }}
                    </div>
                  </div>
                </div>
              </VCardText>
            </VCard>
          </VMenu>
        </div>
      </div>
    </VCard>

    <!-- A counting rule that matches nothing makes its column read 0 with no other sign. -->
    <VAlert
      v-if="showTabs && countingProblems.length"
      type="warning"
      variant="tonal"
      icon="tabler-alert-triangle"
      class="mb-4"
    >
      <div class="font-weight-bold mb-1">
        {{ t("alkokh.doctorPerformance.settings.warnings.bannerTitle") }}
      </div>
      <ul class="dp-problems">
        <li
          v-for="problem in countingProblems"
          :key="problem.key"
        >
          <strong v-if="problem.metric">{{ problem.metric }}:</strong> {{ problem.text }}
        </li>
      </ul>
      <template #append>
        <VBtn
          v-if="ctx.canEditSettings.value"
          size="small"
          variant="tonal"
          color="warning"
          prepend-icon="tabler-settings"
          @click="ctx.openSettings('counting')"
        >
          {{ t("alkokh.doctorPerformance.settings.warnings.fix") }}
        </VBtn>
      </template>
    </VAlert>

    <VAlert
      v-if="showTabs && missingMetricNames"
      type="info"
      variant="tonal"
      density="compact"
      icon="tabler-flask-off"
      class="mb-4"
    >
      {{ t("alkokh.doctorPerformance.states.missingMetrics", { metrics: missingMetricNames }) }}
    </VAlert>

    <AppTabs
      v-if="showTabs"
      v-model="activeTab"
      :items="tabs"
      variant="underline"
      sync-mode="query"
      sync-key="tab"
      default-tab="table"
      card
      tabs-class="px-4"
    />

    <!-- Before the first answer, and when it fails, there are no tabs to show. -->
    <VCard
      v-else
      elevation="0"
      border
    >
      <VCardText class="dp-state">
        <template v-if="firstLoad">
          <CatMascot
            pose="play"
            color="orange"
            :size="120"
          />
          <span class="text-medium-emphasis">{{ t("alkokh.doctorPerformance.states.loading") }}</span>
        </template>
        <template v-else-if="ctx.state.value === 'not_deployed'">
          <CatMascot
            pose="sit"
            mood="grumpy"
            color="grey"
            :size="120"
          />
          <VAlert
            type="info"
            variant="tonal"
            icon="tabler-info-circle"
          >
            {{ t("alkokh.doctorPerformance.states.not_deployed") }}
          </VAlert>
        </template>
        <template v-else>
          <CatMascot
            pose="sit"
            mood="grumpy"
            color="black"
            :size="120"
          />
          <VAlert
            type="error"
            variant="tonal"
          >
            {{ t("alkokh.doctorPerformance.states.failed") }}
            <template #append>
              <VBtn
                size="small"
                variant="text"
                @click="ctx.load"
              >
                {{ t("alkokh.doctorPerformance.actions.retry") }}
              </VBtn>
            </template>
          </VAlert>
        </template>
      </VCardText>
    </VCard>

    <DoctorPerfDrilldownDialog />
    <DoctorPerfSettingsDialog />
  </div>
</template>

<style scoped>
.dp-page :deep(.app-tabs__window) {
  margin-block-start: 16px;
  overflow: visible;
}

.dp-head {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding-block: 20px 4px;
  padding-inline: 20px;
  background:
    radial-gradient(circle at 92% 30%, rgba(var(--v-theme-warning), 0.18), transparent 40%),
    radial-gradient(circle at 0% 0%, rgba(var(--v-theme-primary), 0.12), transparent 45%);
}

.dp-head__text {
  flex: 1 1 280px;
  padding-block-end: 12px;
}

.dp-head__mascot {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  margin-block-end: -10px;
}

.dp-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding-block: 14px;
  padding-inline: 20px;
  border-block-start: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.dp-filters__item {
  flex: 1 1 170px;
  max-inline-size: 240px;
}

.dp-filters__item--wide {
  max-inline-size: 340px;
}

.dp-filters__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-inline-start: auto;
}

.dp-problems {
  margin: 0;
  padding-inline-start: 1.1rem;
}

.dp-def {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-block-end: 10px;
}

.dp-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-block: 48px;
}

@media (max-width: 600px) {
  .dp-head__mascot {
    display: none;
  }

  .dp-filters__item,
  .dp-filters__item--wide {
    max-inline-size: none;
  }
}
</style>
